import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

const SEVERITIES = ['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
const STATUSES = ['ALL', 'OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'FALSE_POSITIVE'];

const SEV_STYLE = {
  CRITICAL: { bg: '#fee2e2', color: '#991b1b' },
  HIGH:     { bg: '#ffedd5', color: '#9a3412' },
  MEDIUM:   { bg: '#fef3c7', color: '#92400e' },
  LOW:      { bg: '#dbeafe', color: '#1e40af' },
  INFO:     { bg: '#f3f4f6', color: '#374151' },
};

const STATUS_STYLE = {
  OPEN:          { bg: '#fee2e2', color: '#991b1b' },
  ACKNOWLEDGED:  { bg: '#fef3c7', color: '#92400e' },
  RESOLVED:      { bg: '#d1fae5', color: '#065f46' },
  FALSE_POSITIVE:{ bg: '#f3f4f6', color: '#6b7280' },
};

// Evidence gate: confirmed findings (proven with a stored negative control) are admissible as audit
// evidence; candidates (exposure/unproven) are shown for triage but never mark a compliance control.
const VERDICTS = ['ALL', 'CONFIRMED', 'CANDIDATE'];
const VERDICT_META = {
  CONFIRMED: { label: 'Evidence', bg: '#d1fae5', color: '#065f46', icon: 'fa-check-circle',
    title: 'Confirmed with a stored negative control — admissible as audit evidence; can mark a control.' },
  CANDIDATE: { label: 'Triage', bg: '#fef3c7', color: '#92400e', icon: 'fa-flask',
    title: 'Exposure or unproven — shown for triage only; never marks a compliance control until promoted.' },
};

const MITRE = {
  T1078: { name: 'Valid Accounts',                      tactic: 'Privilege Escalation', url: 'https://attack.mitre.org/techniques/T1078' },
  T1190: { name: 'Exploit Public-Facing Application',   tactic: 'Initial Access',       url: 'https://attack.mitre.org/techniques/T1190' },
  T1195: { name: 'Supply Chain Compromise',             tactic: 'Initial Access',       url: 'https://attack.mitre.org/techniques/T1195' },
  T1213: { name: 'Data from Information Repositories',  tactic: 'Collection',           url: 'https://attack.mitre.org/techniques/T1213' },
  T1530: { name: 'Data from Cloud Storage Object',      tactic: 'Collection',           url: 'https://attack.mitre.org/techniques/T1530' },
};

export default function FindingsTab({ orgId, defaultExpandedId, onDeepLinkConsumed }) {
  const { t } = useTranslation();
  const [findings, setFindings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [severity, setSeverity] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [verdict, setVerdict] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [assignForm, setAssignForm] = useState({ assignedTo: '', dueDate: '' });

  const fetchFindings = useCallback(() => {
    setLoading(true);
    const params = { page, size: 50 };
    if (severity !== 'ALL') params.severity = severity;
    if (status !== 'ALL') params.status = status;
    if (verdict !== 'ALL') params.verdict = verdict.toLowerCase();
    if (orgId) params.orgId = orgId;
    axios.get('/iasm/findings', { params })
      .then(r => { setFindings(r.data.content); setTotal(r.data.totalElements); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, severity, status, verdict, orgId]);

  useEffect(() => { fetchFindings(); }, [fetchFindings]);

  // Auto-expand the deep-linked finding once the list loads
  useEffect(() => {
    if (!defaultExpandedId || loading || findings.length === 0) return;
    const match = findings.find(f => f.id === defaultExpandedId);
    if (match) {
      setExpanded(defaultExpandedId);
      onDeepLinkConsumed?.();
      // Scroll to the finding after a short paint delay
      setTimeout(() => {
        const el = document.getElementById(`finding-row-${defaultExpandedId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [findings, loading, defaultExpandedId, onDeepLinkConsumed]);

  const runAudit = () => {
    setAuditing(true);
    setAuditResult(null);
    const params = orgId ? { orgId } : {};
    axios.post('/iasm/audit/run', null, { params })
      .then(r => { setAuditResult(r.data); fetchFindings(); })
      .catch(() => setAuditResult({ message: t('FindingsTab.auditFailed') }))
      .finally(() => setAuditing(false));
  };

  const updateStatus = (id, newStatus) => {
    setUpdating(id);
    setUpdateError(null);
    const params = orgId ? { orgId } : {};
    axios.patch(`/iasm/findings/${id}/status`, { status: newStatus }, { params })
      .then(() => fetchFindings())
      .catch(err => {
        const msg = err?.response?.data?.message || err?.response?.data || err?.message || t('FindingsTab.unknownError');
        setUpdateError(t('FindingsTab.statusUpdateFailed', { status: newStatus, message: msg }));
        console.error('[findings] updateStatus error:', err);
      })
      .finally(() => setUpdating(null));
  };

  const openAssign = (f) => {
    setAssigning(f.id);
    setAssignForm({ assignedTo: f.assignedTo || '', dueDate: f.dueDate || '' });
  };

  const submitAssign = (id) => {
    const params = orgId ? { orgId } : {};
    axios.patch(`/iasm/findings/${id}/assign`, assignForm, { params })
      .then(() => { setAssigning(null); fetchFindings(); })
      .catch(() => {});
  };

  return (
    <div>
      {updateError && (
        <div style={{
          margin: '0 0 12px', padding: '10px 14px', borderRadius: '6px',
          background: '#fee2e2', color: '#991b1b', fontSize: '13px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span><i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }} />{updateError}</span>
          <button onClick={() => setUpdateError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 700 }}>✕</button>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: '#374151' }}>
          {t('FindingsTab.subtitle')}
        </div>
        <button onClick={runAudit} disabled={auditing}
          style={{
            padding: '8px 18px', background: auditing ? '#e5e7eb' : '#7c3aed',
            color: auditing ? '#9ca3af' : '#fff', border: 'none', borderRadius: '6px',
            cursor: auditing ? 'default' : 'pointer', fontSize: '13px', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
          <i className={`fas ${auditing ? 'fa-spinner fa-spin' : 'fa-search-plus'}`} />
          {auditing ? t('FindingsTab.runningAudit') : t('FindingsTab.runSecurityAudit')}
        </button>
      </div>

      {auditResult && (
        <div style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: '8px',
          background: auditResult.totalNewFindings > 0 ? '#fef3c7' : '#f0fdf4',
          border: `1px solid ${auditResult.totalNewFindings > 0 ? '#fde68a' : '#bbf7d0'}`,
          fontSize: '13px', color: auditResult.totalNewFindings > 0 ? '#92400e' : '#15803d',
          display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center',
        }}>
          <span><i className="fas fa-clipboard-check" style={{ marginRight: '6px' }} />{auditResult.message}</span>
          {['iam', 'network', 'code', 'storage'].map(k => auditResult[k + 'Findings'] !== undefined && (
            <span key={k} style={{ fontSize: '12px', padding: '2px 8px', background: 'rgba(0,0,0,0.06)', borderRadius: '4px' }}>
              {k.toUpperCase()}: <strong>{auditResult[k + 'Findings']}</strong>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginRight: '4px' }}>Lane:</span>
        {VERDICTS.map(v => {
          const meta = VERDICT_META[v];
          const active = verdict === v;
          return (
            <button key={v} title={meta?.title}
              onClick={() => { setVerdict(v); setSeverity('ALL'); setStatus('ALL'); setPage(0); }}
              style={{
                padding: '5px 12px', borderRadius: '5px',
                border: '1px solid ' + (active && meta ? meta.color : '#e5e7eb'),
                cursor: 'pointer', fontSize: '12px', fontWeight: active ? 700 : 500,
                background: active ? (meta?.bg || '#111827') : '#fff',
                color: active ? (meta?.color || '#374151') : '#374151',
                display: 'inline-flex', alignItems: 'center', gap: '5px',
              }}>
              {meta?.icon && <i className={`fas ${meta.icon}`} style={{ fontSize: '10px' }} />}
              {v === 'ALL' ? 'All' : meta.label}
            </button>
          );
        })}
        {verdict === 'CANDIDATE' && (
          <span style={{ fontSize: '11px', color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', padding: '2px 8px', borderRadius: '4px' }}>
            <i className="fas fa-info-circle" style={{ marginRight: '4px' }} />Triage findings never mark a compliance control until proven.
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {SEVERITIES.map(s => (
            <button key={s} onClick={() => { setSeverity(s); setVerdict('ALL'); setPage(0); }}
              style={{
                padding: '5px 12px', borderRadius: '5px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: severity === s ? 600 : 400,
                background: severity === s ? (SEV_STYLE[s]?.bg || '#2563eb') : '#f3f4f6',
                color: severity === s ? (SEV_STYLE[s]?.color || '#fff') : '#374151',
              }}>{s}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {STATUSES.map(s => (
            <button key={s} onClick={() => { setStatus(s); setVerdict('ALL'); setPage(0); }}
              style={{
                padding: '5px 12px', borderRadius: '5px', border: '1px solid #e5e7eb', cursor: 'pointer',
                fontSize: '12px', fontWeight: status === s ? 600 : 400,
                background: status === s ? '#111827' : '#fff',
                color: status === s ? '#fff' : '#374151',
              }}>{s.replace(/_/g, ' ')}</button>
          ))}
        </div>
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#6b7280' }}>{t('FindingsTab.findingsCount', { count: total })}</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />{t('FindingsTab.loadingFindings')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {findings.map(f => (
            <div key={f.id} id={`finding-row-${f.id}`} style={{
              border: expanded === f.id ? '1px solid #3b82f6' : '1px solid #e5e7eb',
              borderRadius: '8px', overflow: 'hidden',
              background: '#fff',
            }}>
              <div
                onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '12px' }}
              >
                <span style={{
                  ...SEV_STYLE[f.severity], padding: '2px 8px', borderRadius: '4px',
                  fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap', marginTop: '2px',
                }}>{f.severity}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{f.title}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                    <span>{f.findingType?.replace(/_/g, ' ')}</span>
                    {f.asset && <span>· <strong>{f.asset.name}</strong> ({f.asset.assetType})</span>}
                    {f.mitreTechniqueId && (
                      <span style={{ color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                        · <i className="fas fa-shield-virus" style={{ fontSize: '10px' }} />
                        {f.mitreTechniqueId}{MITRE[f.mitreTechniqueId] ? ` · ${MITRE[f.mitreTechniqueId].tactic}` : ''}
                      </span>
                    )}
                    <span style={{
                      padding: '1px 6px', borderRadius: '4px', fontSize: '11px',
                      background: f.overdue ? '#fee2e2' : '#f3f4f6',
                      color: f.overdue ? '#991b1b' : '#6b7280',
                    }}>
                      <i className={`fas ${f.overdue ? 'fa-exclamation-circle' : 'fa-clock'}`} style={{ marginRight: '3px' }} />
                      {t('FindingsTab.daysOpen', { count: f.daysOpen })}
                    </span>
                    {f.jiraId && (
                      <a
                        href={f.jiraIssueUrl || `https://markups.atlassian.net/browse/${f.jiraId}`}
                        target="_blank" rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '1px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                          background: '#e0f2fe', color: '#0369a1', textDecoration: 'none',
                          border: '1px solid #bae6fd',
                        }}
                      >
                        <i className="fab fa-jira" style={{ fontSize: '10px' }} />
                        {f.jiraId}
                      </a>
                    )}
                    {f.assignedTo
                      ? <span style={{ color: '#7c3aed', fontSize: '11px' }}><i className="fas fa-user" style={{ marginRight: '3px' }} />{f.assignedTo}</span>
                      : <span style={{ color: '#9ca3af', fontSize: '11px' }}><i className="fas fa-user-slash" style={{ marginRight: '3px' }} />{t('FindingsTab.unassigned')}</span>
                    }
                    {f.dueDate && (
                      <span style={{ fontSize: '11px', color: f.overdue ? '#dc2626' : '#374151' }}>
                        <i className="fas fa-calendar-alt" style={{ marginRight: '3px' }} />{t('FindingsTab.dueDate', { date: f.dueDate })}
                      </span>
                    )}
                  </div>
                </div>
                {f.verdict && VERDICT_META[String(f.verdict).toUpperCase()] && (
                  <span title={VERDICT_META[String(f.verdict).toUpperCase()].title} style={{
                    background: VERDICT_META[String(f.verdict).toUpperCase()].bg,
                    color: VERDICT_META[String(f.verdict).toUpperCase()].color,
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700,
                    whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px',
                  }}>
                    <i className={`fas ${VERDICT_META[String(f.verdict).toUpperCase()].icon}`} style={{ fontSize: '10px' }} />
                    {VERDICT_META[String(f.verdict).toUpperCase()].label}
                  </span>
                )}
                <span style={{
                  ...STATUS_STYLE[f.status], padding: '2px 8px', borderRadius: '4px',
                  fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
                }}>{f.status}</span>
              </div>

              {expanded === f.id && (
                <div style={{ borderTop: '1px solid #f3f4f6', padding: '14px 16px', background: '#f9fafb' }}>
                  {f.remediationSteps && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>
                          <i className="fas fa-wrench" style={{ marginRight: '6px', color: '#10b981' }} />{t('FindingsTab.remediationSteps')}
                        </span>
                        <button
                          onClick={() => navigator.clipboard.writeText(f.remediationSteps)}
                          style={{ padding: '3px 10px', fontSize: '11px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', color: '#374151', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <i className="fas fa-copy" style={{ fontSize: '10px' }} />{t('FindingsTab.copy')}
                        </button>
                      </div>
                      <pre style={{ margin: 0, padding: '10px 12px', background: '#1e293b', color: '#e2e8f0', borderRadius: '6px', fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.6' }}>
                        {f.remediationSteps}
                      </pre>
                    </div>
                  )}
                  {f.mitreTechniqueId && MITRE[f.mitreTechniqueId] && (
                    <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MITRE ATT&CK</span>
                          <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 600, color: '#1e1b4b' }}>
                            {f.mitreTechniqueId} — {MITRE[f.mitreTechniqueId].name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                            {t('FindingsTab.tacticLabel')} <strong style={{ color: '#7c3aed' }}>{MITRE[f.mitreTechniqueId].tactic}</strong>
                          </div>
                        </div>
                        <a href={MITRE[f.mitreTechniqueId].url} target="_blank" rel="noreferrer"
                          style={{ fontSize: '11px', color: '#7c3aed', textDecoration: 'none', border: '1px solid #ddd6fe', padding: '4px 10px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                          <i className="fas fa-external-link-alt" style={{ marginRight: '4px' }} />{t('FindingsTab.viewOnMitre')}
                        </a>
                      </div>
                    </div>
                  )}
                  {f.complianceControls?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                        <i className="fas fa-shield-alt" style={{ marginRight: '6px', color: '#3b82f6' }} />{t('FindingsTab.complianceControls')}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {f.complianceControls.map(c => (
                          <span key={c} style={{
                            padding: '2px 8px', background: '#eff6ff', color: '#1d4ed8',
                            borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                          }}>{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {f.status !== 'RESOLVED' && (
                      <button onClick={() => updateStatus(f.id, 'RESOLVED')} disabled={updating === f.id}
                        style={{ padding: '6px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        {updating === f.id ? '...' : t('FindingsTab.markResolved')}
                      </button>
                    )}
                    {f.status === 'OPEN' && (
                      <button onClick={() => updateStatus(f.id, 'ACKNOWLEDGED')} disabled={updating === f.id}
                        style={{ padding: '6px 14px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        {t('FindingsTab.acknowledge')}
                      </button>
                    )}
                    {f.status === 'OPEN' && (
                      <button onClick={() => updateStatus(f.id, 'FALSE_POSITIVE')} disabled={updating === f.id}
                        style={{ padding: '6px 14px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        {t('FindingsTab.falsePositive')}
                      </button>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); assigning === f.id ? setAssigning(null) : openAssign(f); }}
                      style={{ padding: '6px 14px', background: '#ede9fe', color: '#6d28d9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                      <i className="fas fa-user-tag" style={{ marginRight: '5px' }} />{t('FindingsTab.assign')}
                    </button>
                  </div>

                  {assigning === f.id && (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>{t('FindingsTab.assignToLabel')}</label>
                        <input
                          value={assignForm.assignedTo}
                          onChange={e => setAssignForm(p => ({ ...p, assignedTo: e.target.value }))}
                          placeholder={t('FindingsTab.assignToPlaceholder')}
                          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '180px' }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>{t('FindingsTab.dueDateLabel')}</label>
                        <input
                          type="date"
                          value={assignForm.dueDate}
                          onChange={e => setAssignForm(p => ({ ...p, dueDate: e.target.value }))}
                          style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
                        />
                      </div>
                      <button onClick={() => submitAssign(f.id)}
                        style={{ padding: '7px 16px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        {t('FindingsTab.save')}
                      </button>
                      <button onClick={() => setAssigning(null)}
                        style={{ padding: '7px 12px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>
                        {t('FindingsTab.cancel')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {findings.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              {t('FindingsTab.emptyState')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
